/**
 * ChannelBadge Component
 *
 * Displays a toggleable badge for notification channels (Digest/Instant).
 * Shows loading state during toggle operations.
 */

import { Text, Icon, Tooltip, Spinner, InlineStack } from '@shopify/polaris';
import { ClockIcon, SendIcon } from '@shopify/polaris-icons';
import type { ChannelBadgeProps } from './types';
import { CHANNEL_LABELS, ARIA_LABELS } from './constants';

interface ChannelBadgeWithHandlerProps extends ChannelBadgeProps {
  /** Handler for toggling the channel */
  onToggle: () => void;
}

export function ChannelBadge({
  type,
  status,
  isLoading = false,
  onToggle,
}: ChannelBadgeWithHandlerProps) {
  const label = CHANNEL_LABELS[type];
  const isEnabled = status.enabled;
  const icon = type === 'digest' ? ClockIcon : SendIcon;
  const tone = type === 'digest' ? 'info' : 'success';

  const tooltipContent = isEnabled
    ? `${label} notifications enabled - click to disable`
    : `Enable ${label.toLowerCase()} notifications`;

  const ariaLabel = ARIA_LABELS.channelToggle(label, isEnabled);

  return (
    <Tooltip content={tooltipContent}>
      <span>
        <button
          type="button"
          onClick={onToggle}
          disabled={isLoading}
          className={`channel-badge ${isEnabled ? 'channel-badge-enabled' : 'channel-badge-disabled'}`}
          aria-label={ariaLabel}
          aria-pressed={isEnabled}
        >
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <InlineStack gap="050" blockAlign="center" wrap={false}>
              <Icon source={icon} tone={isEnabled ? tone : 'subdued'} />
              <Text as="span" variant="bodySm" tone={isEnabled ? undefined : 'subdued'}>
                {label}
              </Text>
            </InlineStack>
          )}
        </button>
      </span>
    </Tooltip>
  );
}
